'use client'

import { useEffect, useState } from 'react'
import { AuthCard } from '@/components/auth/AuthCard'
import { useBranding } from '@/hooks/useBranding'
import Link from 'next/link'
import { JoinCommunityButton } from '@/components/community/JoinCommunityButton'

type LoginPageClientProps = {
  sellerId: number
  sellerSlug?: string
  alreadyAuthenticated?: boolean
  next?: string
  miniappTitle?: string
  miniappSlug?: string
}

export function LoginPageClient({
  sellerId,
  sellerSlug,
  alreadyAuthenticated = false,
  next = '/start',
  miniappTitle,
  miniappSlug,
}: LoginPageClientProps) {
  const { brand } = useBranding(sellerId)

  const [activating, setActivating] = useState(
    alreadyAuthenticated && sellerId > 0
  )

  const [activationError, setActivationError] =
    useState<string | null>(null)

  const [joinedPending, setJoinedPending] = useState(false)
  const [retryActivation, setRetryActivation] = useState(0)

  useEffect(() => {
    if (!alreadyAuthenticated || !sellerId) return

    let cancelled = false

    async function activate() {
      try {
        const res = await fetch('/api/communities/switch', {
          method: 'POST',
          credentials: 'include',
          cache: 'no-store',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ sellerId }),
        })

        const data = await res.json().catch(() => null)

        if (!res.ok || !data?.ok) {
          throw new Error(
            data?.error || 'SELLER_ACCESS_DENIED'
          )
        }

        if (!cancelled) {
          // Recarga completa para reconstruir
          // los providers con el seller correcto.
          window.location.replace('/home')
        }
      } catch (error) {
        if (!cancelled) {
          setActivationError(
            error instanceof Error
              ? error.message
              : 'No se pudo activar la comunidad'
          )
          setActivating(false)
        }
      }
    }

    activate()

    return () => {
      cancelled = true
    }
  }, [alreadyAuthenticated, sellerId, retryActivation])

  const brandLogo = brand?.logos?.square || ''
  const brandName =
    miniappTitle || brand?.name || 'Hubee'

  /**
   * Usa aquí la propiedad primary real que ya entrega useBranding.
   * Los fallbacks permiten cubrir las estructuras más comunes.
   */
  const primaryColor =
    brand?.colors?.primary ||
    '#6366f1'

  return (
    <main
      className="dark relative min-h-dvh overflow-hidden bg-[#111111] text-white"
      style={
        {
          '--login-primary': primaryColor,
          colorScheme: 'dark',
        } as React.CSSProperties
      }
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 hidden overflow-hidden md:block login-header-glow"
      >
        <svg
          className="w-full"
          viewBox="0 0 1440 427"
          fill="none"
          preserveAspectRatio="none"
        >
          <defs>
            <filter id="loginGlow" x="-100%" y="-100%" width="300%" height="300%">
              <feGaussianBlur stdDeviation="70" />
            </filter>

            <linearGradient id="loginGradient">
              <stop offset="0%" stopColor={`${primaryColor}55`} />
              <stop offset="55%" stopColor={primaryColor} />
              <stop offset="100%" stopColor={`${primaryColor}88`} />
            </linearGradient>
          </defs>

          <g filter="url(#loginGlow)">
            <path
              d="M1517 286.5C1517 70.2805 1152.56 -105 703 -105C253.44 -105 -111 70.2805 -111 286.5"
              stroke="url(#loginGradient)"
              strokeWidth="300"
            />
          </g>
        </svg>
      </div>

      <div className="relative z-10 flex min-h-dvh w-full items-center justify-center px-6 py-10 sm:px-8">
        <div className="w-full max-w-[470px]">
          {/* Branding */}
          <Link
            href="/"
            className="mb-10 flex flex-col items-center justify-center gap-4 text-center transition-opacity hover:opacity-90"
          >
            {brandLogo ? (
              <img
                src={brandLogo}
                alt={brandName}
                className="h-16 w-16 shrink-0 rounded-xl object-cover"
              />
            ) : (
              <div
                className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl text-xl font-bold text-white"
                style={{
                  backgroundColor: 'var(--login-primary)',
                }}
              >
                {brandName.charAt(0).toUpperCase()}
              </div>
            )}

            <span className="max-w-full text-center text-[30px] font-bold leading-tight tracking-[-0.04em] text-white break-words">
              {brandName}
            </span>
          </Link>

          {/* Login o activación de comunidad */}
          {activating ? (
            <div className="py-10 text-center text-sm text-white/70">
              {miniappTitle
                ? 'Abriendo tu aplicación...'
                : 'Abriendo tu comunidad...'}
            </div>
          ) : activationError ? (
            <div className="space-y-5 text-center">
              {joinedPending ? (
                <>
                  <h2 className="text-xl font-semibold">
                    Solicitud enviada
                  </h2>
                  <p className="text-sm text-white/70">
                    Estamos esperando a que el administrador
                    apruebe tu acceso.
                  </p>
                </>
              ) : activationError === 'SELLER_ACCESS_DENIED' ? (
                <>
                  <h2 className="text-xl font-semibold">
                    Acceso a la comunidad
                  </h2>

                  <p className="text-sm text-white/70">
                    Si todavía no perteneces a esta comunidad,
                    puedes solicitar tu ingreso.
                  </p>

                  <JoinCommunityButton
                    sellerId={sellerId}
                    approvalRequired={false}
                    onJoined={(status) => {
                      if (status === 'pending') {
                        setJoinedPending(true)
                      } else {
                        setActivationError(null)
                        setActivating(true)
                        setRetryActivation((value) => value + 1)
                      }
                    }}
                  />
                </>
              ) : (
                <p className="text-sm text-white/70">
                  No pudimos abrir esta comunidad.
                </p>
              )}

              <a
                href={sellerSlug ? `/${encodeURIComponent(sellerSlug)}` : '/'}
                className="inline-flex h-11 items-center justify-center rounded-xl border border-white/20 px-6 text-sm font-semibold text-white"
              >
                Volver al perfil
              </a>
            </div>
          ) : (
            <div className="space-y-5">
              <AuthCard
                sellerId={sellerId || undefined}
                variant="inline"
                chrome="none"
                theme="dark"
                next={next}
              />

              {miniappSlug && (
                <div className="text-center">
                  <p className="text-sm text-white/50">
                    ¿Aún no tienes una cuenta?
                  </p>

                  <a
                    href={`/api/miniapp/purchase?slug=${encodeURIComponent(miniappSlug)}`}
                    className="mt-2 inline-flex items-center justify-center text-sm font-bold text-white underline underline-offset-4 transition hover:opacity-70"
                  >
                    Obtener acceso a esta aplicación
                  </a>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </main>
  )
}